import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Slide,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useDispatch, useSelector } from 'react-redux';
import { getMyDueMilestones, markMilestonesSeen } from '../../redux/features/milestoneAchievementSlice';

//DEEP STAGE THE CARDS SIT ON . DECORATIVE ONLY — IT ENCODES NOTHING.
const STAGE = { from: '#241b57', mid: '#3730a3', to: '#0f766e' };
const GOLD = '#fbbf24';
const CONFETTI = ['#fbbf24', '#4ade80', '#60a5fa', '#f472b6', '#a78bfa', '#fb923c'];

/* ─────────────────────────── motion ─────────────────────────── */

const aurora = keyframes`
  0%   { transform: translate3d(-8%, -6%, 0) scale(1); }
  50%  { transform: translate3d(8%, 6%, 0) scale(1.18); }
  100% { transform: translate3d(-8%, -6%, 0) scale(1); }
`;
const fall = keyframes`
  0%   { transform: translateY(-60px) rotate(0deg); opacity: 0; }
  10%  { opacity: 1; }
  100% { transform: translateY(560px) rotate(680deg); opacity: 0; }
`;
const rise = keyframes`
  from { transform: translateY(18px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
`;
//OVERSHOOTS THEN SETTLES , SO A CARD LANDS RATHER THAN JUST APPEARING
const springIn = keyframes`
  0%   { transform: translateY(26px) scale(0.86); opacity: 0; }
  55%  { transform: translateY(-6px) scale(1.04); opacity: 1; }
  80%  { transform: translateY(2px) scale(0.99); }
  100% { transform: translateY(0) scale(1); opacity: 1; }
`;
const haloPulse = keyframes`
  0%   { transform: scale(0.9); opacity: 0.7; }
  70%  { transform: scale(1.4); opacity: 0; }
  100% { transform: scale(1.4); opacity: 0; }
`;
const sheen = keyframes`
  0%   { transform: translateX(-120%) rotate(18deg); }
  100% { transform: translateX(320%) rotate(18deg); }
`;
const shimmer = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50%      { background-position: 100% 50%; }
`;

const SlideUp = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

//EVERY ANIMATION IN HERE IS DECORATION , SO ALL OF IT IS DROPPED FOR REDUCED MOTION
const CALM = { '@media (prefers-reduced-motion: reduce)': { animation: 'none' } };

/* ─────────────────────────── chrome ─────────────────────────── */

const Aurora = () => (
  <Box aria-hidden sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
    {[
      { c: '#6d28d9', s: 460, t: '-18%', l: '-12%', d: '14s' },
      { c: '#0d9488', s: 420, t: '42%', l: '62%', d: '18s' },
      { c: '#b45309', s: 320, t: '58%', l: '6%', d: '22s' },
    ].map((b, i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          top: b.t,
          left: b.l,
          width: b.s,
          height: b.s,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(b.c, 0.55)} 0%, transparent 68%)`,
          filter: 'blur(26px)',
          animation: `${aurora} ${b.d} ease-in-out infinite`,
          ...CALM,
        }}
      />
    ))}
  </Box>
);

const Confetti = ({ count = 34 }) => {
  //DETERMINISTIC — Math.random() HERE WOULD RESHUFFLE EVERY RE RENDER
  const pieces = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        left: (i * 29) % 100,
        delay: ((i * 17) % 30) / 10,
        duration: 3 + ((i * 11) % 22) / 10,
        size: 5 + (i % 4) * 2,
        colour: CONFETTI[i % CONFETTI.length],
        round: i % 3 === 0,
      })),
    [count],
  );
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        '@media (prefers-reduced-motion: reduce)': { display: 'none' },
      }}
    >
      {pieces.map((p, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            top: -24,
            left: `${p.left}%`,
            width: p.size,
            height: p.size * (p.round ? 1 : 1.7),
            borderRadius: p.round ? '50%' : '2px',
            bgcolor: p.colour,
            opacity: 0,
            animation: `${fall} ${p.duration}s linear ${p.delay}s infinite`,
          }}
        />
      ))}
    </Box>
  );
};

/* ─────────────────────────── achiever card ─────────────────────────── */

/**
 * one achiever. `solo` gives the single-achiever case a larger, centred treatment;
 * otherwise the cards tile so a whole batch reads as one announcement.
 */
const AchieverCard = ({ milestone, index, solo }) => {
  const avatar = solo ? 128 : 86;
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width: solo ? '100%' : { xs: '100%', sm: 268 },
        p: solo ? 3 : 2.25,
        borderRadius: 4,
        textAlign: 'center',
        //GLASS PANEL OVER THE AURORA
        bgcolor: alpha('#ffffff', 0.1),
        border: '1px solid',
        borderColor: alpha('#ffffff', 0.22),
        backdropFilter: 'blur(10px)',
        boxShadow: `0 14px 40px ${alpha('#000', 0.32)}`,
        //EACH CARD LANDS SLIGHTLY AFTER THE ONE BEFORE IT
        animation: `${springIn} 620ms cubic-bezier(.2,.8,.3,1.1) ${140 + index * 110}ms both`,
        ...CALM,
      }}
    >
      {/* ONE SLOW LIGHT SWEEP ACROSS THE GLASS */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: '38%',
          background: `linear-gradient(90deg, transparent, ${alpha('#fff', 0.16)}, transparent)`,
          animation: `${sheen} 3.4s ease-in-out ${900 + index * 160}ms infinite`,
          '@media (prefers-reduced-motion: reduce)': { display: 'none' },
        }}
      />

      <Stack spacing={solo ? 1.5 : 1.15} alignItems="center" sx={{ position: 'relative' }}>
        <Box sx={{ position: 'relative', width: avatar + 16, height: avatar + 16, display: 'grid', placeItems: 'center' }}>
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 4,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: alpha(GOLD, 0.75),
              animation: `${haloPulse} 2.6s ease-out ${index * 220}ms infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0.25 },
            }}
          />
          <Avatar
            src={milestone.user_photo_url || undefined}
            alt={milestone.user_name || 'Employee'}
            sx={{
              width: avatar,
              height: avatar,
              fontSize: avatar / 2.6,
              fontWeight: 700,
              border: '3px solid',
              borderColor: alpha('#fff', 0.92),
              boxShadow: `0 10px 26px ${alpha('#000', 0.45)}`,
            }}
          >
            {milestone.user_name?.[0] ?? '?'}
          </Avatar>
          <Box
            sx={{
              position: 'absolute',
              right: solo ? 8 : 2,
              bottom: solo ? 8 : 4,
              width: solo ? 40 : 30,
              height: solo ? 40 : 30,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: GOLD,
              color: '#3f2d00',
              boxShadow: `0 5px 14px ${alpha('#000', 0.42)}`,
            }}
          >
            <Icon icon="mdi:trophy" width={solo ? 22 : 17} height={solo ? 22 : 17} />
          </Box>
        </Box>

        <Stack spacing={0.25} alignItems="center">
          <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: solo ? 24 : 16 }} noWrap={!solo}>
            {milestone.user_name ?? '—'}
          </Typography>
          {/* THE VIEWER'S OWN MILESTONE IS CALLED OUT , SINCE EVERYONE SEES EVERY CARD */}
          {milestone.is_mine ? (
            <Chip
              size="small"
              label="That's you"
              sx={{
                height: 19,
                fontSize: 10,
                fontWeight: 700,
                color: '#3f2d00',
                bgcolor: GOLD,
              }}
            />
          ) : milestone.user_emp_id ? (
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.7) }}>
              {milestone.user_emp_id}
            </Typography>
          ) : null}
        </Stack>

        <Typography
          sx={{
            fontWeight: 700,
            color: '#fff',
            fontSize: solo ? 17 : 14,
            lineHeight: 1.35,
          }}
        >
          {milestone.milestone_title}
        </Typography>

        {milestone.milestone_description ? (
          <Typography
            variant="body2"
            sx={{
              color: alpha('#fff', 0.82),
              fontSize: solo ? 14 : 12.5,
              //KEEPS A LONG DESCRIPTION FROM MAKING ONE TILE TOWER OVER THE OTHERS
              display: '-webkit-box',
              WebkitLineClamp: solo ? 4 : 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {milestone.milestone_description}
          </Typography>
        ) : null}

        {milestone.milestone_reward ? (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
              mt: 0.5,
              px: 1.5,
              py: 0.6,
              borderRadius: 999,
              bgcolor: alpha(GOLD, 0.16),
              border: '1px solid',
              borderColor: alpha(GOLD, 0.45),
            }}
          >
            <Icon icon="mdi:gift-outline" width="16" height="16" color={GOLD} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: GOLD }}>
              {milestone.milestone_reward}
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};

/* ─────────────────────────── the announcement ─────────────────────────── */

/**
 * the milestone announcement.
 * mounted once in the app shell. EVERY employee is shown every recently published
 * milestone — whoever it belongs to — all of them together in one window, exactly once.
 * @author Akhil E M
 * @createdAt 27-08-2026
 * @updates 27-08-2026(Akhil EM),
 * @type component
 */
const MilestoneCelebration = () => {
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state.milestoneAchievement.due);
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    dispatch(getMyDueMilestones());
  }, [dispatch]);

  //FREEZE THE BATCH WHEN IT FIRST ARRIVES . THE SLICE EMPTIES due ON DISMISS , AND WITHOUT
  //THIS THE CARDS WOULD VANISH BEFORE THE CLOSING TRANSITION HAD PLAYED.
  const [batch, setBatch] = useState([]);
  useEffect(() => {
    if (data?.length && !batch.length) {
      setBatch(data);
      setOpen(true);
    }
  }, [data, batch.length]);

  const solo = batch.length === 1;
  const mineCount = batch.filter((m) => m.is_mine).length;

  const handleClose = async () => {
    if (dismissing) return;
    setDismissing(true);
    setOpen(false);
    //MARK SEEN ONLY ON AN EXPLICIT DISMISS . IF THIS FAILS THE ANNOUNCEMENT STAYS DUE AND
    //SHOWS AGAIN NEXT LOGIN , WHICH IS THE SAFE DIRECTION TO FAIL IN.
    await dispatch(markMilestonesSeen(batch.map((m) => m.milestone_achievement_id)));
    setDismissing(false);
  };

  if (!batch.length) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={SlideUp}
      maxWidth={solo ? 'xs' : 'md'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 5,
          overflow: 'hidden',
          position: 'relative',
          background: `linear-gradient(150deg, ${STAGE.from} 0%, ${STAGE.mid} 52%, ${STAGE.to} 100%)`,
          color: '#fff',
        },
      }}
    >
      <Aurora />
      <Confetti />

      {/* EXPLICIT CLOSE . THE "Celebrate" BUTTON IS THE HAPPY PATH , BUT WITHOUT A VISIBLE X
          THE ONLY OTHER WAYS OUT ARE THE BACKDROP AND ESC , WHICH ARE NOT DISCOVERABLE.
          IT DISMISSES EXACTLY LIKE THE BUTTON , SO CLOSING NEVER LEAVES IT TO REAPPEAR. */}
      <Tooltip title="Close">
        <IconButton
          onClick={handleClose}
          disabled={dismissing}
          aria-label="Close milestone announcement"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 3,
            color: '#fff',
            bgcolor: alpha('#000', 0.28),
            border: '1px solid',
            borderColor: alpha('#fff', 0.35),
            backdropFilter: 'blur(6px)',
            '&:hover': { bgcolor: alpha('#000', 0.45) },
          }}
        >
          <Icon icon="mdi:close" width="20" height="20" />
        </IconButton>
      </Tooltip>

      <Stack
        spacing={3}
        sx={{
          position: 'relative',
          px: { xs: 2.5, sm: 4 },
          pt: 4,
          pb: 3.5,
          maxHeight: '86vh',
          overflowY: 'auto',
          //SLIM STYLED SCROLLBAR , SO A LARGE BATCH DOES NOT GET A CHUNKY NATIVE BAR
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#fff', 0.28), borderRadius: 999 },
        }}
      >
        <Stack spacing={1} alignItems="center" sx={{ textAlign: 'center', animation: `${rise} 420ms ease both`, ...CALM }}>
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(GOLD, 0.18),
              border: '1px solid',
              borderColor: alpha(GOLD, 0.5),
            }}
          >
            <Icon icon="mdi:party-popper" width="27" height="27" color={GOLD} />
          </Box>

          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: 24, sm: 30 },
              lineHeight: 1.15,
              //SLOW GOLD SHIMMER ACROSS THE HEADLINE
              background: `linear-gradient(90deg, #fff 0%, ${GOLD} 30%, #fff 60%, ${GOLD} 100%)`,
              backgroundSize: '260% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: `${shimmer} 5s ease-in-out infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', WebkitTextFillColor: '#fff' },
            }}
          >
            {solo ? 'Milestone Achieved' : 'Celebrating our achievers'}
          </Typography>

          <Typography variant="body2" sx={{ color: alpha('#fff', 0.82), maxWidth: 520 }}>
            {solo
              ? mineCount
                ? 'Congratulations on your achievement.'
                : 'Join us in congratulating a colleague on their milestone.'
              : `${batch.length} colleagues have reached a new milestone${
                  mineCount ? ', including you' : ''
                }. Congratulations to all of them.`}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          justifyContent="center"
          alignItems="stretch"
          sx={{ gap: 2 }}
        >
          {batch.map((milestone, i) => (
            <AchieverCard
              key={milestone.milestone_achievement_id}
              milestone={milestone}
              index={i}
              solo={solo}
            />
          ))}
        </Stack>

        <Stack alignItems="center">
          <Button
            onClick={handleClose}
            disabled={dismissing}
            variant="contained"
            sx={{
              px: 5,
              py: 1,
              borderRadius: 999,
              fontWeight: 800,
              bgcolor: '#fff',
              color: STAGE.from,
              animation: `${rise} 520ms ease ${260 + batch.length * 90}ms both`,
              '&:hover': { bgcolor: alpha('#fff', 0.9) },
              ...CALM,
            }}
          >
            {dismissing ? 'Closing...' : 'Celebrate 🎉'}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default MilestoneCelebration;
